// The guest's own bubble — thread opener and any additional guest turns
// (docs/06-ux-rules.md "Thread": "Guest comment / chat turns → plain
// bubble, left"; P5 D2). Avatar shows 2-letter initials (never the merchant
// logo or Thanx mark — docs/06-ux-rules.md "Avatar rule").
//
// DOM/style ported from prototype v70's `.cv-guest` (VISUAL-SPEC.md §1):
// a header row (name + time, indented to line up with the bubble rather
// than the avatar) above an avatar+bubble row, not the single flex row v69
// used.
import Avatar from 'components/Avatar'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { useStyletron } from 'styletron-react'
import { formatShortDateTime } from 'utilities/dateFormatters'

const t = buildTranslate('feedback_inbox.thread')

const AVATAR_SIZE_PX = 34
const AVATAR_GAP_PX = 11
// Lines the header row up with the bubble, not the avatar (VISUAL-SPEC.md
// §1: `.cv-guest-hd{margin-left:45px}` = avatar 34px + row gap 11px).
const HEADER_INDENT_PX = AVATAR_SIZE_PX + AVATAR_GAP_PX
const GUEST_MESSAGE_MAX_WIDTH = '96%'

export interface GuestMessageProps {
  name: string
  initials: string
  text: string
  at: string
  // Only true for the guest's OPENING rating comment (buildThreadItems.ts)
  // — wraps it in curly quotes via the locale string, per VISUAL-SPEC.md
  // §1 "Quote marks". Later guest chat turns render unquoted.
  quoted?: boolean
}

const GuestMessage = ({
  name,
  initials,
  text,
  at,
  quoted,
}: GuestMessageProps) => {
  const [css] = useStyletron()

  return (
    <div className={css({ maxWidth: GUEST_MESSAGE_MAX_WIDTH })}>
      <div
        className={css({
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '12px',
          color: palette.grey70,
          marginLeft: `${HEADER_INDENT_PX}px`,
          marginBottom: '6px',
        })}
      >
        <span>{t('customer_heading', { name })}</span>
        <span>{formatShortDateTime(at)}</span>
      </div>
      <div
        className={css({
          display: 'flex',
          gap: `${AVATAR_GAP_PX}px`,
          alignItems: 'flex-start',
        })}
      >
        <div
          className={css({
            flexShrink: 0,
            width: `${AVATAR_SIZE_PX}px`,
            height: `${AVATAR_SIZE_PX}px`,
            fontSize: '12px',
          })}
        >
          <Avatar
            // v83's fallback avatar is #5a6673; grey70 is kept — white
            // initials pass AA on both, so no new token.
            color={palette.grey70}
            initials={initials}
            borderRadius="9px"
          />
        </div>
        <div
          className={css({
            minWidth: 0,
            flex: 1,
            backgroundColor: palette.white,
            border: `1px solid ${palette.grey20}`,
            borderRadius: '14px',
            padding: '14px 17px',
            fontSize: '14px',
            color: palette.grey90,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          })}
        >
          {quoted ? t('quoted_comment', { text }) : text}
        </div>
      </div>
    </div>
  )
}

export default GuestMessage
